/**
 * Editor API Service
 *
 * Calls the existing ila-api endpoints to fetch version data, permissions,
 * users, and to save/saveAs. Replicates the same API calls Angular made
 * from text-file-data.component.ts and spreadsheet-file-data.component.ts.
 *
 * All requests authenticate via x-auth-token header from authStore.
 */

import { environment } from '@config/environment.config';
import type { GraphSnapshot } from '@/graph/domain/types';
import { authStore } from '@services/authStore.service';

const ILA_API_BASE = environment.getIlaApiBaseUrl();

// =============================================================================
// TYPES
// =============================================================================

export interface VersionEntity {
  _id: string;
  name: string;
  versionNumber: number;
  fullName: string;
  uid: string;
  externalUid?: string;
  description?: string;
  data?: string;
  sfdtData?: string;
  createdBy?: { _id: string; name: string; email: string; profilePhoto?: string };
  updatedBy?: { _id: string; name: string; email: string; profilePhoto?: string };
  owner?: { _id: string; name: string; email: string; profilePhoto?: string };
  createdAt?: string;
  updatedAt?: string;
  isArchived?: boolean;
  isArchivalInProgress?: boolean;
  isMasterCopy?: boolean;
  protocolLocked?: boolean;
  lifecycleLocked?: boolean;
  isSoftDeleted?: boolean;
  isAutosaveEnabled?: boolean;
  textFile?: ParentFile;
  spreadsheetFile?: ParentFile;
  experiment?: { _id: string; name: string; isArchived?: boolean; isArchivalInProgress?: boolean };
  lifecycleProperties?: { 
    currentState: string; 
    lifecycleTemplate: any;
    canEditContent?: boolean;
    isUnderTransition?: boolean;
    color?: string | null;
  };
  allowComment?: boolean;
  isLifecycleHistorySnapshot?: boolean;
  lifecycleHistoryContext?: {
    historyEntryId: string;
    state: string;
    transitionedTo: string;
    changedAt: string;
  };
}

export interface ParentFile {
  _id: string;
  name?: string;
  aim?: string;
  isControlCopy?: boolean;
  masterCopy?: string;
  isMasterCopyEnabled?: boolean;
  enableAutoSave?: boolean;
  overrideAutosaveToUser?: boolean;
  customForm?: {
    id?: string;
  };
}

export interface AutosavePolicy {
  defaultEnabled: boolean;
  allowUserOverride: boolean;
  isTemplatePolicy: boolean;
  templateDefaultEnabled: boolean;
}

export interface UIElement {
  label: string;
  action: string;
  permissionType: string;
}


export interface EntityUser {
    user: User;
    role: any;
    type: string;
    inheritedFrom?: any;
}

export interface User {
  _id: string;
  name?: string;
  uid?: string;
  email?: string;
  externalUid?: string;
  designation?: string;
  secondaryEmail?: string;
  profilePhoto?: string;
  profileSummary?: string;
  education?: any[];
  workExperience?: any[];
  expertiseTags?: string[];
  accomplishments?: string[];
  programmingLangs?: string[];
  languages?: string[];
  others?: string[];
  role?: string;

  misc?: string[];
  rbAlias?: string[];
  rbFollowers?: string[];
  rbFollowing?: string[];
  questionsAsked?: string[];
  questionsAnswered?: string[];
  updatedAt?: string;
  organizations?: string[];
  instances?: string[];
  activities?: string[];
  folders?: string[];
}

export interface ApprovalInfo {
  status?: string;
  data?: any;
}

/** Computed permissions from UI elements — mirrors Angular's Save_Version / SaveAs_Version checks */
export interface EditorPermissions {
  save: boolean;
  saveAs: boolean;
  modify: boolean;
  delete: boolean;
  export: boolean;
  print: boolean;
}

/** Computed locking state — replicates Angular's checkApprovalsActions + processVersionInfo */
export interface LockInfo {
  isLocked: boolean;
  isControlCopy: boolean;
  isMasterCopy: boolean;
  isArchived: boolean;
  isArchivalInProgress: boolean;
  isProtocolLocked: boolean;
  isLifecycleLocked: boolean;
  isApproved: boolean;
  isUnderApproval: boolean;
  lockReason: string | null;
  canEditContent?: boolean | null;
}

export type DocumentType = 'regular' | 'master' | 'control';

/** Everything the React editor needs to initialize — assembled from multiple API calls */
export interface EditorInitData {
  version: VersionEntity;
  permissions: EditorPermissions;
  lockInfo: LockInfo;
  documentType: DocumentType;
  users: EntityUser[];
  currentUser: User | null;
  parentFileId: string;
  approvalStatus: string | null;
  allowVersionCreationDisabled: boolean;
  autosavePolicy: AutosavePolicy;
}

// =============================================================================
// INTERNAL HELPERS
// =============================================================================

/**
 * Shared error class for API failures — lets callers distinguish API errors
 * from network errors via `instanceof`.
 */
export class ApiError extends Error {
  constructor(
    public readonly url: string,
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(`API ${url} failed (${status})`);
    this.name = 'ApiError';
  }
}

/**
 * Validate the fetch response.
 * - Throws on hard HTTP failures (status >= 500, 401, 403, 0/network).
 * - For other status codes, returns the parsed JSON — the backend doesn't
 *   always use HTTP status codes, so callers inspect the body instead.
 */
async function handleResponse<T>(res: Response, url: string): Promise<T> {
  // Network-level or server failures — always throw
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(url, res.status, 'Unauthorized — session may have expired');
  }
  if (res.status >= 500) {
    const text = await res.text().catch(() => res.statusText);
    throw new ApiError(url, res.status, text);
  }

  const json = await res.json();
  return json as T;
}

async function post<T>(url: string, body: Record<string, unknown>): Promise<T> {
  const res = await fetch(`${ILA_API_BASE}${url}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authStore.getAuthHeader(),
    },
    body: JSON.stringify(body),
  });
  return handleResponse<T>(res, url);
}

async function get<T>(url: string): Promise<T> {
  const res = await fetch(`${ILA_API_BASE}${url}`, {
    method: 'GET',
    headers: {
      ...authStore.getAuthHeader(),
    },
  });
  return handleResponse<T>(res, url);
}

function computePermissions(uiElements: UIElement[]): EditorPermissions {
  const labels = new Set(uiElements.map((e) => e.label));
  return {
    save: labels.has('Save_Version'),
    saveAs: labels.has('SaveAs_Version'),
    modify: labels.has('Modify_Version'),
    delete: labels.has('Delete_Version'),
    export: true,
    print: true,
  };
}

function computeLockInfo(version: VersionEntity, approvalStatus: string | null): LockInfo {
  const isArchived = version?.isArchived === true;
  const isArchivalInProgress = version?.isArchivalInProgress === true;
  const isProtocolLocked = version.protocolLocked === true;
  const isLifecycleLocked = version.lifecycleLocked === true;
  const isApproved = approvalStatus === 'COMPLETED' || approvalStatus === 'APPROVED';
  const isUnderApproval = approvalStatus === 'IN_PROGRESS';
  const parentFile = version.textFile || version.spreadsheetFile;
  const isControlCopy = parentFile?.isControlCopy === true;
  const isMasterCopy = version.isMasterCopy === true;
  const canEditContent = version?.lifecycleProperties?.canEditContent ?? true;

  const isLocked =
    isArchived || isArchivalInProgress || isProtocolLocked || isLifecycleLocked || isApproved || isUnderApproval || !canEditContent;

  let lockReason: string | null = null;
  if (isApproved) {
    lockReason =
      'The file cannot be modified as it has been Approved. Click on Save as a New Version to make changes.';
  } else if (isUnderApproval) {
    lockReason =
      'The file cannot be modified as it is under Approval. Click on Save as a New Version to make changes.';
  } else if (isArchived) {
    lockReason = 'The file cannot be modified as it is archived.';
  } else if (isArchivalInProgress) {
    lockReason = 'The file is under archival. Please complete the archival flow.';
  } else if (isLifecycleLocked) {
    lockReason = 'This version is locked by lifecycle status transition. Create a new version to edit.';
  } else if (isProtocolLocked) {
    lockReason = 'This file is protocol-locked and cannot be modified.';
  } else if (!canEditContent) {
    lockReason = 'The file cannot be modified as editing is disabled in the current lifecycle status.';
  }

  return {
    isLocked,
    isControlCopy,
    isMasterCopy,
    isArchived,
    isArchivalInProgress,
    isProtocolLocked,
    isLifecycleLocked,
    isApproved,
    isUnderApproval,
    lockReason,
    canEditContent,
  };
}

function computeDocumentType(version: VersionEntity): DocumentType {
  const parentFile = version.textFile || version.spreadsheetFile;
  if (parentFile?.isControlCopy) return 'control';
  if (version.isMasterCopy) return 'master';
  return 'regular';
}

async function fetchAndComputeAllowVersionCreationDisabled(version: VersionEntity | WetlabVersionEntity): Promise<boolean> {
  const lifecycleProperties = version.lifecycleProperties;
  
  // If no lifecycle properties, version creation is allowed
  if (!lifecycleProperties) {
    console.log('[DEBUG] computeAllowVersionCreationDisabled: No lifecycle properties found, allowing version creation');
    return false;
  }

  const currentStateKey = lifecycleProperties.currentState;
  let lifecycleTemplate = lifecycleProperties.lifecycleTemplate;

  console.log('[DEBUG] computeAllowVersionCreationDisabled: Starting check', {
    versionId: version._id,
    currentStateKey,
    lifecycleTemplateType: typeof lifecycleTemplate,
    templateId: typeof lifecycleTemplate === 'string' ? lifecycleTemplate : lifecycleTemplate?._id,
  });

  // If no current state or template, version creation is allowed
  if (!currentStateKey || !lifecycleTemplate) {
    console.log('[DEBUG] computeAllowVersionCreationDisabled: Missing currentStatus or template', {
      hasCurrentState: !!currentStateKey,
      hasTemplate: !!lifecycleTemplate,
    });
    return false;
  }

  // Get template ID for fetching
  let templateId = '';
  if (typeof lifecycleTemplate === 'string') {
    templateId = lifecycleTemplate;
  } else if (lifecycleTemplate?._id) {
    templateId = lifecycleTemplate._id;
  }

  // If template is incomplete, fetch full template
  if (templateId && (typeof lifecycleTemplate === 'string' || !lifecycleTemplate?.states || lifecycleTemplate.states.length === 0)) {
    console.log('[DEBUG] Template incomplete, fetching full template', { templateId });

    try {
      const response = await get<any>(`/api/v1/entityLifecycleTemplates/${templateId}`);
      const template = response?.data || response;
      
      if (template?.states && Array.isArray(template.states)) {
        lifecycleTemplate = template;
        console.log('[DEBUG] Fetched full template', {
          templateId,
          templateName: lifecycleTemplate.name,
          statesCount: lifecycleTemplate.states.length,
          stateKeys: lifecycleTemplate.states.map((s: any) => s.key),
        });
      } else {
        console.warn('[WARN] Template fetched but no states array', { templateId });
        return false;
      }
    } catch (error) {
      console.error('[ERROR] Failed to fetch template:', { error: error instanceof Error ? error.message : String(error), templateId });
      return false;
    }
  }

  // Get states array
  const states = Array.isArray(lifecycleTemplate?.states) ? lifecycleTemplate.states : [];
  console.log('[DEBUG] Searching for current state', {
    currentStateKey,
    totalStatesInTemplate: states.length,
    stateKeys: states.map((s: any) => s.key),
  });

  // Find the current state by key
  const currentState = states.find((state: any) => state.key === currentStateKey);

  if (!currentState) {
    console.warn('[WARN] Current state not found in template', {
      lookingFor: currentStateKey,
      availableStates: states.map((s: any) => ({ key: s.key, name: s.name })),
    });
    return false; // Allow creation if state not found
  }

  // Extract allowVersionCreation option from current state
  const stateOptions = currentState.controlledDocumentOptions || {};
  const allowVersionCreation = stateOptions.allowVersionCreation;
  
  console.log('[DEBUG] Current state found', {
    currentStateKey,
    stateName: currentState.name,
    stateOptions,
    allowVersionCreationOption: allowVersionCreation,
    allowVersionCreationOptionType: typeof allowVersionCreation,
  });

  // Determine if disabled: true if allowVersionCreation is explicitly FALSE
  // Default to allow creation (true) if not set
  const isDisabled = allowVersionCreation === false;

  console.log('[DEBUG] FINAL DECISION', {
    currentStateKey,
    stateName: currentState.name,
    allowVersionCreation,
    isDisabled,
    reason: isDisabled ? 'allowVersionCreation is FALSE' : 'allowVersionCreation is not FALSE (true or undefined)',
  });

  return isDisabled;
}

function computeAutosavePolicy(version: VersionEntity): AutosavePolicy {
  const parentFile = version.textFile;

  // Only enforce template policy when file is created from a custom text template.
  const isTemplatePolicy = Boolean(parentFile?.customForm?.id);
  if (!isTemplatePolicy) {
    // Non-template: use per-version saved state (default true if not stored yet)
    return {
      defaultEnabled: version.isAutosaveEnabled !== false,
      allowUserOverride: true,
      isTemplatePolicy: false,
      templateDefaultEnabled: true,
    };
  }

  const allowUserOverride = Boolean(parentFile?.overrideAutosaveToUser);
  const templateDefaultEnabled = Boolean(parentFile?.enableAutoSave);
  // When user override is allowed, respect the stored per-version toggle state.
  // Fall back to the template's enableAutoSave default only when override is off.
  const defaultEnabled = allowUserOverride
    ? version.isAutosaveEnabled !== false
    : templateDefaultEnabled;

  return {
    defaultEnabled,
    allowUserOverride,
    isTemplatePolicy: true,
    templateDefaultEnabled,
  };
}

// =============================================================================
// PUBLIC API — TEXT EDITOR
// =============================================================================

/**
 * Fetch everything needed for the text editor to initialize.
 * Mirrors Angular's text-file-data.component.ts flow:
 *   1. POST /api/textFileVersion/get/version
 *   2. POST /api/textFileVersion/ui
 *   3. POST /api/textFile/get/textFile/users
 *   4. POST /api/approvals/entity/TEXTFILE_VERSION/{versionId} (for approval status)
 */
export async function fetchTextEditorInit(versionId: string, historyEntryId?: string): Promise<EditorInitData> {
  console.log('[DEBUG] fetchTextEditorInit: Starting init for versionId:', { versionId });
  
  // 1. Fetch version (includes populated textFile with isControlCopy, isMasterCopyEnabled)
  const versionRes = await post<{ entity: VersionEntity }>(
    '/api/textFileVersion/get/version',
    { versionId, historyEntryId }
  );
  const version = versionRes.entity;
  console.log('[DEBUG] fetchTextEditorInit: Fetched version', { versionId: version._id, hasData: !!version.data, hasSfdtData: !!version.sfdtData, dataLength: version.data?.length, sfdtDataLength: version.sfdtData?.length });
  const parentFileId = version.textFile?._id ?? '';

  // 2–4 in parallel: UI elements, users, approval status
  const [uiElements, users, currentUserRes, approvalInfo] = await Promise.all([
    post<UIElement[]>('/api/textFileVersion/ui', { _id: versionId }),
    post<{ users: EntityUser[] } | EntityUser[]>('/api/textFile/get/textFile/users', { _id: parentFileId }),
    get<User>('/api/users/me').catch(() => null as User | null),
    post<ApprovalInfo>(`/api/approvals/entity/TEXTFILE_VERSION/${versionId}`, {}).catch(
      () => ({ status: undefined } as ApprovalInfo)
    ),
  ]);

  const approvalStatus = approvalInfo?.data?.status ?? approvalInfo?.status ?? null;
  const isHistoricalSnapshot = version.isLifecycleHistorySnapshot === true;
  const livePermissions = computePermissions(uiElements);
  const permissions = isHistoricalSnapshot
    ? { ...livePermissions, save: false, saveAs: false, modify: false }
    : livePermissions;
  const liveLockInfo = computeLockInfo(version, approvalStatus);
  const lockInfo = isHistoricalSnapshot
    ? {
        ...liveLockInfo,
        isLocked: true,
        lockReason: `Viewing ${version.lifecycleHistoryContext?.state || 'historical'} lifecycle content. This snapshot is read-only.`,
      }
    : liveLockInfo;
  const documentType = computeDocumentType(version);
  const allowVersionCreationDisabled = isHistoricalSnapshot
    ? true
    : await fetchAndComputeAllowVersionCreationDisabled(version);
  const autosavePolicy = computeAutosavePolicy(version);

  const userList = Array.isArray(users) ? users : (users as { users: EntityUser[] }).users ?? [];
  const currentUser = currentUserRes ?? null;
  console.log('[DEBUG] fetchTextEditorInit: COMPLETED', {
    versionId: version._id,
    currentState: version.lifecycleProperties?.currentState,
    allowVersionCreationDisabled,
    documentType,
  });
  return { version, permissions, lockInfo, documentType, users: userList, currentUser, parentFileId, approvalStatus, allowVersionCreationDisabled, autosavePolicy };
}

/**
 * Fetch everything needed for the spreadsheet editor to initialize.
 * Mirrors Angular's spreadsheet-file-data.component.ts flow.
 */
export async function fetchSpreadsheetEditorInit(
  versionId: string
): Promise<EditorInitData> {
  const versionRes = await post<{ entity: VersionEntity }>(
    '/api/spreadsheetFileVersion/get/version',
    { versionId }
  );
  const version = versionRes.entity;
  const parentFileId = version.spreadsheetFile?._id ?? '';

  const [uiElements, users, currentUserRes, approvalInfo] = await Promise.all([
    post<UIElement[]>('/api/spreadsheetFileVersion/ui', { _id: versionId }),
    post<{ users: EntityUser[] } | EntityUser[]>('/api/spreadsheetFile/get/spreadsheetFile/users', {
      _id: parentFileId,
    }),
    get<User>('/api/users/me').catch(() => null as User | null),
    post<ApprovalInfo>(
      `/api/approvals/entity/SPREADSHEETFILE_VERSION/${versionId}`,
      {}
    ).catch(() => ({ status: undefined } as ApprovalInfo)),
  ]);

  const approvalStatus = approvalInfo?.data?.status ?? approvalInfo?.status ?? null;
  const permissions = computePermissions(uiElements);
  const lockInfo = computeLockInfo(version, approvalStatus);
  const documentType = computeDocumentType(version);
  const allowVersionCreationDisabled = await fetchAndComputeAllowVersionCreationDisabled(version);

  const userList = Array.isArray(users) ? users : (users as { users: EntityUser[] }).users ?? [];
  const currentUser = currentUserRes ?? null;
  return {
    version,
    permissions,
    lockInfo,
    documentType,
    users: userList,
    currentUser,
    parentFileId,
    approvalStatus, allowVersionCreationDisabled,
    autosavePolicy: {
      defaultEnabled: true,
      allowUserOverride: true,
      isTemplatePolicy: false,
      templateDefaultEnabled: true,
    },
  };
}

// =============================================================================
// SAVE / AUTOSAVE / SAVE-AS — TEXT FILE VERSION
// =============================================================================

/**
 * Full save — POST /api/textFileVersion/update
 * Mirrors Angular's save payload.
 */
export async function saveTextFileVersion(body: {
  _id: string;
  data?: string;
  sfdtData: string;
  commentsOnly?: boolean;
  description?: string;
  filesUploaded?: string[];
  filesRemoved?: string[];
  sessionMentions?: Array<{ users: string[] }>;
  isAutosaveEnabled?: boolean;
}): Promise<VersionEntity> {
  return post<VersionEntity>('/api/textFileVersion/update', body);
}

/**
 * Lightweight autosave — POST /api/textFileVersion/updateContent
 * Only sends sfdtData + _id. Returns { _id, name }.
 */
export async function autosaveTextFileVersion(body: {
  _id: string;
  sfdtData: string;
  isAutosaveEnabled?: boolean;
}): Promise<{ _id: string; name: string }> {
  return post<{ _id: string; name: string }>('/api/textFileVersion/updateContent', body);
}

/**
 * Save As — POST /api/textFileVersion/saveAs
 * Creates a new version from the current one.
 */
export async function saveAsTextFileVersion(body: {
  fromVersionId: string;
  data?: string;
  sfdtData: string;
  versionReason?: string;
  sessionMentions?: Array<{ users: string[] }>;
  prevIsAutosaveEnabled?: boolean;
  newVersionAutosaveDefault?: boolean;
}): Promise<VersionEntity> {
  return post<VersionEntity>('/api/textFileVersion/saveAs', body);
}

export async function convertHtmlToSfdt(html: string): Promise<string> {
  const serviceUrl = environment.getSyncfusionServiceUrl();
  const baseUrl = serviceUrl.endsWith('/') ? serviceUrl.slice(0, -1) : serviceUrl;
  const url = `${baseUrl}/api/DocumentEditor/ConvertHtmlToSfdt`;
  console.log('[DEBUG] convertHtmlToSfdt: Calling endpoint', { url, htmlLength: html?.length });
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      // ...authStore.getAuthHeader(),
    },
    body: JSON.stringify({
      content: html,
      type: '.html',
    }),
  });

  console.log('[DEBUG] convertHtmlToSfdt: Response status', { status: res.status, statusText: res.statusText });
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(url, res.status, 'Unauthorized — session may have expired');
  }
  const text = await res.text().catch(() => '');
  console.log('[DEBUG] convertHtmlToSfdt: Response text received', { textLength: text?.length, textPreview: text?.substring(0, 200) });
  let parsed: { sfdt?: string; error?: string } | null = null;

  if (text) {
    try {
      parsed = JSON.parse(text) as { sfdt?: string; error?: string };
      console.log('[DEBUG] convertHtmlToSfdt: Parsed response', { hasSfdt: !!parsed?.sfdt, hasError: !!parsed?.error, sfdtLength: parsed?.sfdt?.length });
    } catch {
      console.warn('[DEBUG] convertHtmlToSfdt: Failed to parse response as JSON');
      parsed = null;
    }
  }

  if (!res.ok) {
    const message = parsed?.error || text || res.statusText || 'HTML to SFDT conversion failed';
    throw new ApiError(url, res.status, message);
  }

  if (parsed?.error) {
    throw new ApiError(url, res.status, parsed.error);
  }

  if (parsed?.sfdt) {
    return parsed.sfdt;
  }

  // Backward compatibility: accept plain-text SFDT payload.
  return text || '';
}

export async function convertWordToSfdt(file: File): Promise<string> {
  const serviceUrl = environment.getSyncfusionServiceUrl();
  const baseUrl = serviceUrl.endsWith('/') ? serviceUrl.slice(0, -1) : serviceUrl;
  const url = `${baseUrl}/api/DocumentEditor/Import`;
  
  console.log('[DEBUG] convertWordToSfdt: Converting Word file to SFDT', { fileName: file.name, fileSize: file.size, fileType: file.type });
  
  const formData = new FormData();
  formData.append('file', file);
  
  const res = await fetch(url, {
    method: 'POST',
    body: formData,
    // No Content-Type header — let browser set it with boundary for multipart/form-data
  });

  console.log('[DEBUG] convertWordToSfdt: Response status', { status: res.status, statusText: res.statusText });
  if (res.status === 401 || res.status === 403) {
    throw new ApiError(url, res.status, 'Unauthorized — session may have expired');
  }
  
  const text = await res.text().catch(() => '');
  console.log('[DEBUG] convertWordToSfdt: Response text received', { textLength: text?.length, textPreview: text?.substring(0, 200) });
  
  if (!res.ok) {
    const errorMsg = text || res.statusText || 'Word to SFDT conversion failed';
    console.error('[DEBUG] convertWordToSfdt: Conversion failed', { status: res.status, error: errorMsg });
    throw new ApiError(url, res.status, errorMsg);
  }

  if (!text) {
    throw new ApiError(url, res.status, 'Empty response from Word conversion');
  }

  // The Import endpoint returns JSON-serialized SFDT directly (not wrapped)
  try {
    // Validate it's valid JSON by parsing, then return the original string
    JSON.parse(text);
    console.log('[DEBUG] convertWordToSfdt: Successfully converted Word to SFDT', { sfdtLength: text.length });
    return text;
  } catch (e) {
    console.warn('[DEBUG] convertWordToSfdt: Response is not valid JSON', { error: e });
    throw new ApiError(url, 400, 'Invalid SFDT response from Word conversion');
  }
}

// =============================================================================
// CHECKPOINT REFERENCE FILE UPLOAD
// =============================================================================

export interface CheckpointFileUploadResult {
  fileName: string;
  fileUrl: string;
  raw: { filePath?: string; bucket?: string; workspaceId?: string; [k: string]: unknown };
}

/**
 * Upload a checkpoint reference file following the Angular pattern.
 *
 * Flow (matches Angular's InventoryUploadFilesComponent):
 *  1. GET session token from backend → get SAS token + container/bucket details
 *  2. Upload file directly to Azure/S3 using the credentials
 *  3. Save metadata to DB via /api/azure/saveFileInDB
 *
 * Advantage: Custom path handling in frontend, consistent with Angular approach
 */
export async function uploadCheckpointReferenceFile(
  file: File,
  operationTypeId: string
): Promise<CheckpointFileUploadResult> {
  const uploadPath = `CUSTOM_OPERATION/${operationTypeId}/reference-files/`;
  const blobPath = `${uploadPath}${file.name}`;

  // ── Step 1: Get session token from backend ───────────────────────────────
  const tokenUrl = `${ILA_API_BASE}/api/azure/getSessionToken`;
  console.log('[CHECKPOINT] Step 1: Getting session token', { tokenUrl });
  
  let tokenRes;
  try {
    tokenRes = await fetch(tokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authStore.getAuthHeader() },
      body: '{}',
    });
  } catch (err) {
    console.error('[CHECKPOINT] Step 1 FAILED - Fetch error:', err);
    throw new ApiError(tokenUrl, 0, `Network error: ${err instanceof Error ? err.message : String(err)}`);
  }

  console.log('[CHECKPOINT] Step 1 Response:', { status: tokenRes.status, ok: tokenRes.ok });

  if (tokenRes.status === 401 || tokenRes.status === 403) {
    throw new ApiError(tokenUrl, tokenRes.status, 'Unauthorized — session may have expired');
  }
  if (!tokenRes.ok) {
    const text = await tokenRes.text().catch(() => tokenRes.statusText);
    console.error('[CHECKPOINT] Step 1 Error response:', text);
    throw new ApiError(tokenUrl, tokenRes.status, text || 'Failed to get session token');
  }

  const tokenData = await tokenRes.json();
  console.log('[CHECKPOINT] Step 1 Token data received:', {
    hasSasToken: !!tokenData.sasToken,
    hasContainerUrl: !!tokenData.containerUrl,
    containerName: tokenData.containerName,
    isAzurite: tokenData.isAzurite,
  });

  const sasToken = tokenData.sasToken;
  const containerUrl = tokenData.containerUrl;
  const containerName = tokenData.containerName;
  const isAzurite = tokenData.isAzurite;

  if (!containerUrl || !containerName) {
    throw new ApiError(tokenUrl, 500, 'Invalid storage token response: missing container details');
  }

  // ── Step 2: Upload file directly to Azure Blob Storage ───────────────────
  console.log('[CHECKPOINT] Step 2: Uploading file to Azure', { 
    blobPath,
    fileSize: file.size,
    fileType: file.type
  });

  const normalizedContainerUrl = String(containerUrl).replace(/\/+$/, '');
  const encodedBlobPath = blobPath
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/');
  const uploadUrl = `${normalizedContainerUrl}/${encodedBlobPath}${sasToken ? `?${sasToken}` : ''}`;

  let uploadRes: Response;
  try {
    uploadRes = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'x-ms-blob-type': 'BlockBlob',
        'Content-Type': file.type || 'application/octet-stream',
      },
      body: file,
    });
  } catch (err) {
    console.error('[CHECKPOINT] Step 2 FAILED - Upload network error:', err);
    throw new ApiError(uploadUrl, 0, `Blob upload failed: ${err instanceof Error ? err.message : String(err)}`);
  }

  if (!uploadRes.ok) {
    const uploadErrText = await uploadRes.text().catch(() => uploadRes.statusText);
    console.error('[CHECKPOINT] Step 2 FAILED - Upload response error:', {
      status: uploadRes.status,
      body: uploadErrText,
      isAzurite,
    });
    throw new ApiError(uploadUrl, uploadRes.status, uploadErrText || 'Blob upload failed');
  }

  console.log('[CHECKPOINT] Step 2: File uploaded successfully', { isAzurite });

  // ── Step 3: Construct file location (signed URL with SAS token) ──────────
  const fileLocation = `${normalizedContainerUrl}/${encodedBlobPath}${sasToken ? `?${sasToken}` : ''}`;
  console.log('[CHECKPOINT] Step 3: File location constructed');

  // ── Step 4: Save file metadata to DB ─────────────────────────────────────
  const saveUrl = `${ILA_API_BASE}/api/azure/saveFileInDB`;
  const savePayload = {
    context: 'CUSTOM_OPERATION',
    contextId: operationTypeId,
    bucket: containerName,
    path: uploadPath,
    fileName: file.name,
    s3FileName: file.name,
    fileLocation,
    entity: 'S3',
    size: file.size,
    type: file.type,
    filePath: blobPath,
    field: 'files',
  };

  console.log('[CHECKPOINT] Step 4: Saving file metadata', { 
    saveUrl,
    contextId: operationTypeId,
    fileName: file.name,
  });

  let saveRes;
  try {
    saveRes = await fetch(saveUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authStore.getAuthHeader() },
      body: JSON.stringify(savePayload),
    });
  } catch (err) {
    console.error('[CHECKPOINT] Step 4 FAILED - Fetch error:', err);
    throw new ApiError(saveUrl, 0, `Network error: ${err instanceof Error ? err.message : String(err)}`);
  }

  console.log('[CHECKPOINT] Step 4 Response:', { status: saveRes.status, ok: saveRes.ok });

  if (saveRes.status === 401 || saveRes.status === 403) {
    throw new ApiError(saveUrl, saveRes.status, 'Unauthorized — session may have expired');
  }
  if (!saveRes.ok) {
    const text = await saveRes.text().catch(() => saveRes.statusText);
    console.error('[CHECKPOINT] Step 4 Error response:', text);
    throw new ApiError(saveUrl, saveRes.status, text || 'Failed to save file metadata');
  }

  const dbRecord = await saveRes.json();
  console.log('[CHECKPOINT] Step 4 Success:', { 
    fileName: dbRecord?.fileName,
    filePath: dbRecord?.filePath 
  });

  const fileName: string = dbRecord?.fileName || file.name;
  const fileUrl: string = dbRecord?.filePath || blobPath;

  return { fileName, fileUrl, raw: dbRecord };
}

/**
 * Get the default storage container name for a workspace.
 * Used when opening reference files that were uploaded before bucket info was stored.
 * @param workspaceId - The workspace ID
 * @returns The default container name (format: staging-dws-{workspaceId})
 */

/**
 * Get a fresh signed URL for a checkpoint reference file blob path.
 * 
 * If containerName is not provided, fetches it from a fresh session token
 * (same approach as uploadCheckpointReferenceFile). This ensures the method
 * works without callers needing to maintain container info.
 * 
 * Calls backend to generate a new SAS URL with current expiry.
 */
export async function getCheckpointReferenceFileSignedUrl(
  blobName: string,
  containerName?: string
): Promise<{ signedUrl: string }> {
  let resolvedContainerName = containerName;

  // If containerName not provided, fetch fresh session token to get current container
  if (!resolvedContainerName) {
    console.log('[CHECKPOINT] getSignedUrl: Fetching session token to resolve container name');
    
    const tokenUrl = `${ILA_API_BASE}/api/azure/getSessionToken`;
    let tokenRes;
    
    try {
      tokenRes = await fetch(tokenUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authStore.getAuthHeader() },
        body: '{}',
      });
    } catch (err) {
      throw new ApiError(tokenUrl, 0, `Network error fetching session token: ${err instanceof Error ? err.message : String(err)}`);
    }

    if (tokenRes.status === 401 || tokenRes.status === 403) {
      throw new ApiError(tokenUrl, tokenRes.status, 'Unauthorized — session may have expired');
    }
    if (!tokenRes.ok) {
      const text = await tokenRes.text().catch(() => tokenRes.statusText);
      throw new ApiError(tokenUrl, tokenRes.status, text || 'Failed to get session token');
    }

    const tokenData = await tokenRes.json();
    resolvedContainerName = tokenData.containerName;

    if (!resolvedContainerName) {
      throw new ApiError(tokenUrl, 500, 'Invalid storage token response: missing containerName');
    }

    console.log('[CHECKPOINT] getSignedUrl: Resolved container from session token:', { resolvedContainerName });
  }

  // Now call getSignedUrl with the resolved container name
  const authHeader = authStore.getAuthHeader();
  const signedUrlEndpoint = `${ILA_API_BASE}/api/azure/getSignedUrl`;

  const response = await fetch(signedUrlEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeader,
    },
    body: JSON.stringify({ containerName: resolvedContainerName, blobName }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData?.message || 
      `Failed to get signed URL: ${response.statusText}`
    );
  }

  return response.json();
}

// =============================================================================
// SAVE / AUTOSAVE / SAVE-AS — SPREADSHEET FILE VERSION
// =============================================================================

export async function saveSpreadsheetFileVersion(body: {
  _id: string;
  data?: string;
  sfdtData: string;
  description?: string;
  filesUploaded?: string[];
  filesRemoved?: string[];
  sessionMentions?: Array<{ users: string[] }>;
}): Promise<VersionEntity> {
  return post<VersionEntity>('/api/spreadsheetFileVersion/update', body);
}

export async function autosaveSpreadsheetFileVersion(body: {
  _id: string;
  sfdtData: string;
  data?: string;
}): Promise<{ _id: string; name: string }> {
  return post<{ _id: string; name: string }>(
    '/api/spreadsheetFileVersion/updateContent',
    body
  );
}

export async function saveAsSpreadsheetFileVersion(body: {
  fromVersionId: string;
  data?: string;
  sfdtData: string;
  versionReason?: string;
  sessionMentions?: Array<{ users: string[] }>;
}): Promise<VersionEntity> {
  return post<VersionEntity>('/api/spreadsheetFileVersion/saveAs', body);
}

export async function exportSpreadsheetAsExcel(versionId: string): Promise<{ base64: string }> {
  return post<{ base64: string }>('/api/spreadsheetFileVersion/exportToExcel', { versionId });
}

export async function exportSpreadsheetAsPDF(body: {
  versionId: string;
  spreadsheetJson: string;
  fileName: string;
}): Promise<{ pdfContent?: string; data?: string; content?: string }> {
  return post('/api/spreadsheetFile/import/generate-signedPDF', {
    spreadsheetJson: body.spreadsheetJson,
    fileName: body.fileName,
    versionId: body.versionId,
  });
}

// =============================================================================
// AUDIT LOGGING
// =============================================================================

/**
 * Audit log payload for workflow and entity actions.
 */
export interface AuditLogPayload {
  actionType: string;  // e.g., 'CREATE', 'UPDATE', 'DELETE'
  entityType: string;  // e.g., 'Wet-lab Workflow', 'VERSION'
  entityId: string;    // MongoDB ID
  entityUID: string;   // Entity UID (for audit display)
  entityName: string;  // Entity name
  description: string; // Audit message
  pageRoute: string;   // Current page route
  entityDetails?: {
    name?: string;
    fileType?: string;
    versionNumber?: number;
    versionName?: string;
    [k: string]: unknown;
  };
  [k: string]: unknown; // Allow additional string properties
}

/**
 * Log an audit event to the audit trail.
 * POST /api/workspace/audit/log
 */
export async function logAuditEvent(payload: AuditLogPayload): Promise<{ success: boolean }> {
  return post<{ success: boolean }>('/api/workspace/audit/log', payload as Record<string, unknown>);
}

/**
 * Update protocol lock status for a version.
 * Mirrors Angular's /api/version/update/status endpoint.
 */
export async function updateProtocolLockStatus(
  versionId: string,
  uid: string,
  isLocked: boolean
): Promise<VersionEntity> {
  return post<VersionEntity>('/api/version/update/status', {
    _id: versionId,
    uid,
    protocolLocked: isLocked,
  });
}

/**
 * Fetch fresh version data after lock status update.
 * Calls v2 /api/v2/version/get endpoint.
 */
export async function fetchVersionDetails(
  versionId: string,
  uid: string
): Promise<{ entity: WetlabVersionEntity }> {
  return post<{ entity: WetlabVersionEntity }>('/api/version/get/version', {
    version: versionId,
    uid,
  });
}

export function convertSpreadsheetToCSV(spreadsheetJson: string): string {
  try {
    let parsed: any;
    try {
      parsed = JSON.parse(spreadsheetJson);
    } catch {
      return '';
    }

    // Unwrap Syncfusion's Workbook wrapper if present
    const workbook = parsed?.Workbook ?? parsed;
    const rows: any[] = workbook?.sheets?.[0]?.rows ?? workbook?.rows ?? [];

    if (rows.length === 0) return '';

    const csvRows: string[] = [];
    for (const row of rows) {
      if (!row) continue;
      const cells: string[] = (row.cells ?? []).map((cell: any) => {
        if (!cell) return '';
        const value = String(cell?.value ?? '').trim();
        if (value.includes('"') || value.includes(',') || value.includes('\n')) {
          return `"${value.replace(/"/g, '""').replace(/\n/g, ' ')}"`;
        }
        return value;
      });
      const line = cells.join(',');
      if (line.trim()) csvRows.push(line);
    }

    return csvRows.join('\n');
  } catch {
    return '';
  }
}

// =============================================================================
// TYPES — WETLAB / PROTOCOL VERSION
// =============================================================================

export interface WetlabVersionEntity {
  _id: string;
  name: string;
  versionNumber?: number;
  uid?: string;
  graph?: string;
  description?: string;
  protocolLocked?: boolean;
  lifecycleLocked?: boolean;
  isArchived?: boolean;
  isArchivalInProgress?: boolean;
  launchLocked?: boolean;
  isMasterCopy?: boolean;
  isStepsOptimized?: boolean;
  activeStateId?: string;
  createdBy?: { _id: string; name: string; email: string; profilePhoto?: string };
  updatedBy?: { _id: string; name: string; email: string; profilePhoto?: string };
  createdAt?: string;
  updatedAt?: string;
  /** Populated parent experiment / workflow */
  experiment?: {
    _id: string;
    name?: string;
    fileType?: string;
    project?: string; // Project ID (v2 API)
    isControlCopy?: boolean;
    isMasterCopyEnabled?: boolean;
    isArchived?: boolean;
    isArchivalInProgress?: boolean;
    masterCopyProperties?: {
      enableControlCopyVersionCreation?: boolean;
      restrictOneRunForEachControlCopy?: boolean;
    };
  };
  runsCount?: number;
  runs?: any[];
  history?: any[];
  comments?: Array<{
    comment?: string;
    entity?: string;
    commentId?: number;
    new?: boolean;
    addedBy?: { _id?: string; name?: string; email?: string; profilePhoto?: string | null };
    data?: string;
    type?: 'GRAPH' | string;
    timestamp?: number | string;
  }>;
  allComments?: Array<{
    comment?: string;
    entity?: string;
    commentId?: number;
    new?: boolean;
    addedBy?: { _id?: string; name?: string; email?: string; profilePhoto?: string | null };
    data?: string;
    type?: 'GRAPH' | string;
    timestamp?: number | string;
  }>;
  lifecycleProperties?: { 
    currentState: string; 
    lifecycleTemplate: any;
    canEditContent?: boolean;
    isUnderTransition?: boolean;
  };
}

export interface WetlabEditorPermissions {
  save: boolean;
  saveAs: boolean;
  launch: boolean;
  lock: boolean;
  import: boolean;
}

export interface WetlabLockInfo {
  isLocked: boolean;
  isArchived: boolean;
  isArchivalInProgress: boolean;
  isProtocolLocked: boolean;
  isLifecycleLocked: boolean;
  isLaunchLocked: boolean;
  lockReason: string | null;
  runsExist: boolean;
  canEditContent?: boolean;
}

/** Everything the React wetlab workflow editor needs to initialize */
export interface WetlabEditorInitData {
  version: WetlabVersionEntity;
  permissions: WetlabEditorPermissions;
  lockInfo: WetlabLockInfo;
  currentUser: User | null;
  parentExperimentId: string;
  fileType: string;
  allowVersionCreationDisabled: boolean;
}

// =============================================================================
// INTERNAL HELPERS — WETLAB VERSION
// =============================================================================

function computeWetlabPermissions(uiElements: UIElement[]): WetlabEditorPermissions {
  const labels = new Set(uiElements.map((e) => e.label));
  return {
    save: labels.has('Save_Version'),
    saveAs: labels.has('SaveAs_Version'),
    launch: labels.has('Launch_Version'),
    lock: labels.has('Lock'),
    import: labels.has('Import'),
  };
}

function computeWetlabLockInfo(version: WetlabVersionEntity): WetlabLockInfo {
  const isArchived = version?.isArchived === true;
  const isArchivalInProgress = version?.isArchivalInProgress === true;
  const isProtocolLocked = version.protocolLocked === true;
  const isLifecycleLocked = version.lifecycleLocked === true;
  const isLaunchLocked = version.launchLocked === true;
  const runsExist = Array.isArray(version.runs) && version.runs.length > 0;
  const canEditContent = version?.lifecycleProperties?.canEditContent ?? true;


  // Locked if either protocol OR lifecycle lock is set, or archived/archival in progress, or runs exist
  const isLocked = isArchived || isArchivalInProgress || isProtocolLocked || isLifecycleLocked || runsExist;

  let lockReason: string | null = null;
  if (isArchived) {
    lockReason = 'The file cannot be modified as it is archived.';
  } else if (isArchivalInProgress) {
    lockReason = 'The file is under archival. Please complete the archival flow.';
  } else if (isLifecycleLocked) {
    lockReason = 'This version is locked by lifecycle status transition. Create a new version to edit.';
  } else if (isProtocolLocked) {
    lockReason = 'This protocol is locked and cannot be modified.';
  } else if (runsExist) {
    lockReason = 'Version has runs attached and cannot be modified.';
  } else if (!canEditContent) {
    lockReason = 'The file cannot be modified as editing is disabled in the current lifecycle status.';
  }

  return { isLocked, isArchived, isArchivalInProgress, isProtocolLocked, isLifecycleLocked, isLaunchLocked, lockReason, runsExist, canEditContent };
}

// =============================================================================
// PUBLIC API — WETLAB WORKFLOW VERSION
// =============================================================================

/**
 * Fetch everything needed for the wetlab workflow editor to initialize.
 * Mirrors Angular's mxgraph.component.ts flow:
 *   1. POST /api/version/get/version  — fetch version entity (with populated experiment)
 *   2. POST /api/version/ui           — fetch UI elements/permissions
 *   3. GET  /api/users/me             — fetch current user
 *
 * @param versionId  The version _id (route param entityId)
 */
export async function fetchWetlabVersionInit(versionId: string): Promise<WetlabEditorInitData> {
  // 1. Fetch version (populates experiment.fileType, experiment._id)
  const versionRes = await post<{ entity: WetlabVersionEntity }>(
    '/api/version/get/version',
    { version: versionId }
  );
  const version = versionRes.entity;
  const parentExperimentId = version.experiment?._id ?? '';
  const fileType = version.experiment?.fileType ?? 'WETLABWORKFLOW';

  // 2–3 in parallel: UI elements + current user
  const [uiElements, currentUserRes] = await Promise.all([
    post<UIElement[]>('/api/version/ui', {
      _id: parentExperimentId,
      entityType: fileType,
    }),
    get<User>('/api/users/me').catch(() => null as User | null),
  ]);

  const permissions = computeWetlabPermissions(uiElements);
  const lockInfo = computeWetlabLockInfo(version);
  const allowVersionCreationDisabled = await fetchAndComputeAllowVersionCreationDisabled(version);

  console.log('[DEBUG] fetchWetlabVersionInit: COMPLETE', {
    versionId: version._id,
    currentState: version.lifecycleProperties?.currentState,
    allowVersionCreationDisabled,
    templateId: version.lifecycleProperties?.lifecycleTemplate,
  });

  return {
    version,
    permissions,
    lockInfo,
    currentUser: currentUserRes ?? null,
    parentExperimentId,
    fileType,
    allowVersionCreationDisabled,
  };
}

/**
 * Fetch wetlab graph snapshot in maxGraph snapshot format.
 * POST /api/version/get/graphSnapshot
 */
export async function fetchWetlabVersionGraphSnapshot(versionId: string): Promise<GraphSnapshot> {
  const encodedVersionId = encodeURIComponent(versionId);
  const res = await get<{ graph: GraphSnapshot }>(`/api/version/get/graphSnapshot?version=${encodedVersionId}`);

  const graph = res.graph ?? {
    materialNodes: [],
    operationNodes: [],
    flowEdges: [],
    groups: [],
    schemaVersion: '1.0',
  };
  return graph;
}

/**
 * Fetch comments for a version from Comments collection.
 * POST /api/comments/get
 */
export async function fetchWetlabVersionComments(versionId: string): Promise<Array<{
  _id: string;
  comment?: string;
  data?: string;
  type?: string;
  timestamp?: string | number;
  addedBy?: { _id?: string; name?: string; email?: string; profilePhoto?: string | null };
}>> {
  return post<Array<{
    _id: string;
    comment?: string;
    data?: string;
    type?: string;
    timestamp?: string | number;
    addedBy?: { _id?: string; name?: string; email?: string; profilePhoto?: string | null };
  }>>('/api/comments/get', {
    entity: versionId,
    type: 'VERSION',
  });
}

// =============================================================================
// USER SEARCH — WORKSPACE ENTITY USERS
// =============================================================================

export interface EntityUser {
  _id: string;
  name: string;
  email?: string;
  profilePhoto?: string | null;
}

/**
 * Search users with access to a given entity (mirrors Angular's WorkspaceEntityUserEmailSearchService).
 * POST /api/users/searchEntityUsers
 */
export async function searchEntityUsers(
  searchTerm: string,
  entity: string,
  entityId: string,
): Promise<EntityUser[]> {
  if (!searchTerm) return [];
  return post<EntityUser[]>('/api/users/searchEntityUsers', {
    searchTerm,
    entity,
    entityId,
    parentId: null,
  });
}

// =============================================================================
// SAVE / AUTOSAVE — WETLAB WORKFLOW VERSION
// =============================================================================

export interface WetlabSavePayload {
  _id: string;
  uid?: string;
  metadata?: { name?: string };
  allComments?: Array<{
    comment?: string;
    entity?: string;
    commentId?: number;
    new?: boolean;
    addedBy?: { _id?: string; email?: string; name?: string; profilePhoto?: string | null };
    data?: string;
    type?: 'GRAPH' | string;
    timestamp?: number;
  }>;
  graph: {
    materialNodes: unknown[];
    operationNodes: unknown[];
    flowEdges: unknown[];
    groups: unknown[];
  };
  selectedNameType?: string;
  isStepsOptimized?: boolean;
}

/**
 * Full save for a wetlab workflow version.
 * POST /api/version/update
 */
export async function saveWetlabVersion(body: WetlabSavePayload): Promise<WetlabVersionEntity> {
  return post<WetlabVersionEntity>('/api/version/update', body as unknown as Record<string, unknown>);
}

/**
 * Autosave (lightweight) for a wetlab workflow version.
 * POST /api/version/updateGraph
 */
export async function autosaveWetlabVersion(body: {
  _id: string;
  graph: {
    materialNodes: unknown[];
    operationNodes: unknown[];
    flowEdges: unknown[];
    groups: unknown[];
  };
}): Promise<{ _id: string }> {
  return post<{ _id: string }>('/api/version/updateGraph', body as unknown as Record<string, unknown>);
}

/**
 * Save as new version for a wetlab workflow.
 * POST /api/version/save-as (v1 API)
 * Creates a new version (v2, v3, etc.) from the current version.
 */
export async function saveAsWetlabVersion(body: {
  fromVersionId: string;
  graph: {
    materialNodes?: unknown[];
    operationNodes?: unknown[];
    flowEdges?: unknown[];
    groups?: unknown[];
    schemaVersion?: string;
  } | { vertices: unknown[]; edges: unknown[] };
  procedure?: string;
  procedureMaterials?: unknown;
  selectedNameType?: string;
  isStepsOptimized?: boolean;
  versionReason?: string;
  allComments?: unknown[];
}): Promise<WetlabVersionEntity> {
  return post<WetlabVersionEntity>('/api/version/save-as', body);
}

// =============================================================================
// CREATE FOLDER & WORKFLOW — SAVE AS NEW WORKFLOW
// =============================================================================

export interface CreateFolderRequest {
  _id: string;
  dataFileType: string;
  parentEntityType: string;
  dataFileName: string;
  parentId: string;
  isNotSubFolder: boolean;
}

export interface CreateFolderResponse {
  _id: string;
  name: string;
  dataFileName: string;
  dataFileType: string;
  parentPathPrefix?: string;
  parentEntityType?: string;
  parentEntityId?: string;
}

export interface CreateWorkflowRequest {
  projectId: string;
  fileType: string;
  parentEntityType: string;
  parentEntityId: string;
  name: string;
  versionData?: {
    name?: string;
    procedureMaterials?: Record<string, unknown>;
    procedure?: string;
    allComments?: unknown[];
    graph?: {
      vertices: unknown[];
      edges: unknown[];
    };
    experiment?: unknown;
    _id?: string;
  };
  isControlCopy?: boolean;
  auditCreateSource?: string;
  sourceWorkflowName?: string;
  sourceVersionNumber?: number;
}

export interface CreateWorkflowResponse {
  _id: string;
  name: string;
  uid?: string;
  fileType: string;
  versions?: Array<{ _id: string; versionNumber: number }>;
  _flexiFolderWrapper?: {
    _id: string;
    dataFileName: string;
    meta?: { properties?: { entityId: string } };
  };
}

/**
 * Create a folder (FlexiFolder).
 * POST /api/flexiFolder/create
 */
export async function createFolder(body: CreateFolderRequest): Promise<CreateFolderResponse> {
  return post<CreateFolderResponse>('/api/flexiFolder/create', body as unknown as Record<string, unknown>);
}

/**
 * Create a new workflow (experiment/file).
 * POST /api/fileType/create
 */
export async function createWorkflow(body: CreateWorkflowRequest): Promise<CreateWorkflowResponse> {
  return post<CreateWorkflowResponse>('/api/fileType/create', body as unknown as Record<string, unknown>);
}

/**
 * Fetch experiment details to get project ID and other metadata.
 * POST /api/v2/experiment/get
 */
export async function fetchExperimentDetails(experimentId: string): Promise<{ 
  _id: string; 
  project: string; 
  name: string; 
  fileType: string;
  [key: string]: unknown;
}> {
  return post<{ _id: string; project: string; name: string; fileType: string; [key: string]: unknown }>(
    '/api/v2/experiment/get',
    { experimentId }
  ).catch(async () => {
    // Fallback: Try the older endpoint
    return post<{ _id: string; project: string; name: string; fileType: string; [key: string]: unknown }>(
      '/api/experiment/get',
      { _id: experimentId }
    );
  });
}
